
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Network, Database, Shield, Code, FileText, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Database as SupabaseDatabase } from "@/integrations/supabase/types";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import InquiryForm from "@/components/services/InquiryForm";

type Service = SupabaseDatabase['public']['Tables']['services']['Row'];

interface ContentPage {
  id: string;
  title: string;
  slug: string;
  description: string;
  content: string | null;
  features: string[];
  benefits: string[];
  image_url?: string;
  type: string;
}

export default function ServiceDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const { data: service, isLoading, error } = useQuery({
    queryKey: ["service", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("services")
        .select("*")
        .eq("slug", slug)
        .single();

      if (error) {
        throw error;
      }

      return data as Service;
    },
  });

  const getIconForService = (title: string) => {
    switch (title.toLowerCase()) {
      case "software development":
        return <Code className="h-16 w-16 text-indigo-500" />;
      case "it infrastructure":
        return <Network className="h-16 w-16 text-blue-500" />;
      case "cloud services":
        return <Database className="h-16 w-16 text-purple-500" />;
      case "erp solutions":
        return <FileText className="h-16 w-16 text-green-500" />;
      case "cyber security":
        return <Shield className="h-16 w-16 text-red-500" />;
      default:
        return <Share2 className="h-16 w-16 text-gray-500" />;
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-12 px-4">
        <div className="animate-pulse">
          <div className="h-10 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-6 bg-gray-200 rounded w-3/4 mb-8"></div>
          <div className="h-64 bg-gray-200 rounded mb-8"></div>
          <div className="h-6 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
        </div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="container mx-auto py-12 px-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Service Not Found</h2>
          <p className="mb-6">
            Sorry, we couldn't find the service you're looking for.
          </p>
          <Button onClick={() => navigate("/services")}>
            Back to Services
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-12 px-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-4 mb-6">
            {getIconForService(service.title)}
            <h1 className="text-3xl md:text-4xl font-bold">{service.title}</h1>
          </div>

          <p className="text-lg text-gray-700 dark:text-gray-300 mb-8">
            {service.description}
          </p>

          <div className="prose prose-lg max-w-none dark:prose-invert mb-12">
            <h2 className="text-2xl font-bold mb-4">What We Offer</h2>
            <p>
              Our {service.title} services are designed to help your business
              thrive in today's competitive landscape. We provide comprehensive
              solutions tailored to your specific needs.
            </p>

            <h3 className="text-xl font-bold mt-8 mb-4">Key Features</h3>
            <ul className="space-y-2">
              {service.features.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <h3 className="text-xl font-bold mt-8 mb-4">Technologies</h3>
            <div className="flex flex-wrap gap-2 mb-8">
              {service.technologies?.map((tech, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-8">
            <Sheet>
              <SheetTrigger asChild>
                <Button size="lg" className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white">
                  Get Started
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[90vw] sm:w-[540px] overflow-y-auto curved-xl">
                <SheetHeader>
                  <SheetTitle>Inquire about {service.title}</SheetTitle>
                  <SheetDescription>
                    Please fill out the form below and we'll get back to you shortly.
                  </SheetDescription>
                </SheetHeader>
                <InquiryForm service={service.title} formType="service" />
              </SheetContent>
            </Sheet>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg sticky top-24">
            <h3 className="text-xl font-bold mb-4">Why Choose Us</h3>
            <Separator className="mb-4" />
            <ul className="space-y-4">
              <li className="flex items-start">
                <span className="mr-2 text-green-500">✓</span>
                <span>Expert team with years of experience</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 text-green-500">✓</span>
                <span>Customized solutions for your business</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 text-green-500">✓</span>
                <span>Ongoing support and maintenance</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 text-green-500">✓</span>
                <span>Competitive pricing and flexible plans</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 text-green-500">✓</span>
                <span>Proven track record of success</span>
              </li>
            </ul>

            <Separator className="my-6" />

            <div className="bg-indigo-50 dark:bg-indigo-900/30 p-4 rounded-lg">
              <h4 className="font-bold mb-2">Ready to get started?</h4>
              <p className="text-sm mb-4">
                Contact us today for a free consultation and quote.
              </p>
              <Sheet>
                <SheetTrigger asChild>
                  <Button className="w-full">Contact Us</Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[90vw] sm:w-[540px] overflow-y-auto curved-xl">
                  <SheetHeader>
                    <SheetTitle>Contact Us</SheetTitle>
                    <SheetDescription>
                      Please fill out the form below and we'll get back to you shortly.
                    </SheetDescription>
                  </SheetHeader>
                  <InquiryForm service={service.title} formType="contact" />
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
